// This code is for a React application
import { DefaultConfig } from './LayoutConfig';

const LAYOUT_CONFIG_KEY = process.env.REACT_APP_BASE_LAYOUT_CONFIG_KEY || 'LayoutConfig';

const getLayoutFromLocalStorage = () => {
  const localStorageItem = localStorage.getItem(LAYOUT_CONFIG_KEY);
  if (localStorageItem) {
    try {
      return JSON.parse(localStorageItem);
    } catch (error) {
      console.error(error);
    }
  }
  return DefaultConfig;
};

const setLayoutIntoLocalStorage = (layoutConfig) => {
  try {
    localStorage.setItem(LAYOUT_CONFIG_KEY, JSON.stringify(layoutConfig));
  } catch (error) {
    console.error(error);
  }
};

const getEmptyCssClasses = () => {
  return {
    header: [],
    headerContainer: [],
    headerMobile: [],
    headerMenu: [],
    aside: [],
    asideMenu: [],
    asideToggle: [],
    toolbar: [],
    toolbarContainer: [],
    content: [],
    contentContainer: [],
    footerContainer: [],
    sidebar: [],
    pageTitle: [],
    pageContainer: [],
  };
};

const getEmptyHTMLAttributes = () => {
  return {
    asideMenu: new Map(),
    headerMobile: new Map(),
    headerMenu: new Map(),
    headerContainer: new Map(),
    pageTitle: new Map(),
  };
};

const getEmptyCSSVariables = () => {
  return {
    body: new Map(),
  };
};

class LayoutSetup {
  static isLoaded = false;
  static config = getLayoutFromLocalStorage();
  static classes = getEmptyCssClasses();
  static attributes = getEmptyHTMLAttributes();
  static cssVariables = getEmptyCSSVariables();

  static initCSSClasses() {
    LayoutSetup.classes = getEmptyCssClasses();
  }

  static initHTMLAttributes() {
    LayoutSetup.attributes = Object.assign({}, getEmptyHTMLAttributes());
  }

  static initCSSVariables() {
    LayoutSetup.cssVariables = getEmptyCSSVariables();
  }

  static initConfig(layoutConfig) {
    let updatedConfig = LayoutSetup.initLayoutSettings(layoutConfig);
    updatedConfig = LayoutSetup.initToolbarSetting(updatedConfig);
    return LayoutSetup.initWidthSettings(updatedConfig);
  }

  static initLayoutSettings(layoutConfig) {
    const updatedConfig = { ...layoutConfig };
    // clear body classes
    const bodyClasses = document.body.classList.value.split(' ');
    bodyClasses.forEach((cssClass) => document.body.classList.remove(cssClass));
    // clear body attributes
    const bodyAttributes = document.body.getAttributeNames().filter((attribute) => attribute.indexOf('data-') > -1);
    bodyAttributes.forEach((attribute) => document.body.removeAttribute(attribute));
    document.body.setAttribute('style', '');
    document.body.setAttribute('id', 'sai_app_body');
    document.body.setAttribute('data-sai-app-layout', updatedConfig.layoutType);
    document.body.setAttribute('data-sai-name', 'metronic');
    document.body.classList.add('app-default');

    const pageWidth = updatedConfig.app?.general?.pageWidth;
    if (updatedConfig.layoutType === 'light-header' || updatedConfig.layoutType === 'dark-header') {
      if (pageWidth === 'default') {
        const header = updatedConfig.app?.header;
        if (header && header.default && header.default.container) {
          header.default.container = 'fixed';
        }

        const toolbar = updatedConfig.app?.toolbar;
        if (toolbar) {
          toolbar.container = 'fixed';
        }

        const content = updatedConfig.app?.content;
        if (content) {
          content.container = 'fixed';
        }

        const footer = updatedConfig.app?.footer;
        if (footer) {
          footer.container = 'fixed';
        }

        const updatedApp = {
          ...updatedConfig.app,
          ...header,
          ...toolbar,
          ...content,
          ...footer,
        };
        return { ...updatedConfig, app: updatedApp };
      }
    }
    return updatedConfig;
  }

  static initToolbarSetting(layoutConfig) {
    const updatedConfig = { ...layoutConfig };
    const appHeaderDefaultContent = updatedConfig.app?.header?.default?.content;
    if (appHeaderDefaultContent === 'page-title') {
      const toolbar = updatedConfig.app?.toolbar;
      if (toolbar) {
        toolbar.display = false;
        const updatedApp = { ...updatedConfig.app, ...toolbar };
        return { ...updatedConfig, app: updatedApp };
      }
      return updatedConfig;
    }

    const pageTitle = updatedConfig.app?.pageTitle;
    if (pageTitle) {
      pageTitle.description = false;
      pageTitle.breadCrumb = true;
      const updatedApp = { ...updatedConfig.app, ...pageTitle };
      return { ...updatedConfig, app: updatedApp };
    }

    return updatedConfig;
  }

  static initWidthSettings(layoutConfig) {
    const updatedConfig = { ...layoutConfig };
    const pageWidth = updatedConfig.app?.general?.pageWidth;
    if (!pageWidth || pageWidth === 'default') {
      return layoutConfig;
    }

    const header = updatedConfig.app?.header;
    if (header && header.default) {
      header.default.container = pageWidth;
    }
    const toolbar = updatedConfig.app?.toolbar;
    if (toolbar) {
      toolbar.container = pageWidth;
    }
    const content = updatedConfig.app?.content;
    if (content) {
      content.container = pageWidth;
    }
    const footer = updatedConfig.app?.footer;
    if (footer) {
      footer.container = pageWidth;
    }
    const updatedApp = {
      ...updatedConfig.app,
      ...header,
      ...toolbar,
      ...content,
      ...footer,
    };
    return { ...updatedConfig, app: updatedApp };
  }

  static updatePartialConfig(fieldsToUpdate) {
    const config = LayoutSetup.config;
    const updatedConfig = { ...config, ...fieldsToUpdate };
    LayoutSetup.initCSSClasses();
    LayoutSetup.initCSSVariables();
    LayoutSetup.initHTMLAttributes();
    LayoutSetup.isLoaded = false;
    LayoutSetup.config = LayoutSetup.initConfig(Object.assign({}, updatedConfig));
    LayoutSetup.isLoaded = true; // remove loading there
    return updatedConfig;
  }

  static setConfig(layoutConfig) {
    setLayoutIntoLocalStorage(layoutConfig);
  }

  static bootstrap = (() => {
    LayoutSetup.updatePartialConfig(LayoutSetup.config);
  })();
}

export {
  LayoutSetup,
  getLayoutFromLocalStorage,
  setLayoutIntoLocalStorage,
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
};